import { formatCodeableConcept, type WithId } from '@medplum/core';
import type { ServiceRequest } from '@medplum/fhirtypes';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../../utils/date';
import { RecordListItem } from './RecordListItem';

export type ServiceRequestsListProps = {
  serviceRequests: WithId<ServiceRequest>[];
  onSelectServiceRequest?: (id: string) => void;
  selectedServiceRequestId?: string;
};

export const ServiceRequestsList = ({
  serviceRequests,
  onSelectServiceRequest,
  selectedServiceRequestId,
}: ServiceRequestsListProps): React.ReactElement => {
  const { t } = useTranslation('records');

  if (serviceRequests.length === 0) {
    return (
      <div className="mx-auto w-full max-w-4xl">
        <p className="py-8 text-center text-sm text-muted-foreground">{t('noServiceRequestsFound')}</p>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-4xl">
      <div className="space-y-2" aria-label={t('serviceRequests')}>
        {serviceRequests.map((sr) => {
          const name = formatCodeableConcept(sr.code) || t('unknownServiceRequest');
          const status = sr.status || t('unknown');
          const category = formatCodeableConcept(sr.category?.at(0)) || null;
          const authoredOn = sr.authoredOn ? formatDate(sr.authoredOn) : null;
          const performer = sr.performer?.at(0)?.display ?? null;

          const priority = sr.priority && sr.priority !== 'routine' ? sr.priority : null;
          const description = [
            status,
            priority,
            category,
            performer && `${t('performedByLabel')} ${performer}`,
            authoredOn && `${t('dateLabel')} ${authoredOn}`,
          ]
            .filter(Boolean)
            .join(' · ');

          return (
            <RecordListItem
              key={sr.id}
              title={name}
              description={description}
              onClick={onSelectServiceRequest ? (): void => onSelectServiceRequest(sr.id) : undefined}
              selected={selectedServiceRequestId === sr.id}
            />
          );
        })}
      </div>
    </div>
  );
};
